import axios from "axios";
import { Observable } from "rxjs";
import { ObservableHandler } from "../../../handlers";
import { parseIdFilter } from "../../../handlers/filter";
import { isServicePage } from "../../../handlers/page";
import { pricingStationGroupFromService, pricingStationGroupBillingInfoFromService, pricingStationGroupBillingInfoToService, toServiceUpsertStationGroupCpmRequestFromStationGroup } from "../../../models/verticalBilling";
import { StationGroupsFetchFailed, StationGroupsSaveFailed } from "./errors";
import { NotImplementedError } from "../../../errors";
import { toStationGroupPricingQueryParams, toStationGroupDetailQueryParams } from "../filters";
class PricingStationGroups extends ObservableHandler {
    constructor(sdk) {
        super(sdk, "station-groups", { atomize: true });
        this.url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/station-groups`;
    }
    findItems(filters, sort) {
        return new Observable((subscriber) => {
            const idFilter = parseIdFilter(filters);
            const promise = idFilter.size === 1
                ? this.getStationGroup(idFilter.values().next().value).then((res) => [
                    res.stationGroup
                ])
                : this.getStationGroupList(filters, sort);
            promise
                .then((result) => {
                subscriber.next(result);
                subscriber.complete();
            })
                .catch((error) => {
                subscriber.error(error);
            });
        });
    }
    /**
     * @param filters - Filters for search and pagination.
     * @param sort - Optional sort configuration.
     * @return A promise resolving to array of station groups or Page of station groups.
     */
    async getStationGroupList(filters = {}, sort) {
        const queryParams = toStationGroupPricingQueryParams(filters, sort);
        const url = `${this.url}${queryParams}`;
        const pageSize = filters?.paging?.size || 50;
        return axios
            .get(url, {
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => {
            const stationGroups = res.data?.map((item) => this.toStationGroupList(item)) || [];
            if (isServicePage(res)) {
                return {
                    page: {
                        count: res.paging_info.count,
                        token: res.paging_info.token,
                        size: pageSize
                    },
                    data: stationGroups
                };
            }
            return stationGroups;
        })
            .catch((error) => {
            throw new StationGroupsFetchFailed(error);
        });
    }
    /**
     * @param stationGroupId - The id of a station group to get.
     * @param filters - Optional filters for search and pagination of nested products.
     * @param sort - Optional sort configuration for nested products (name, cpm).
     * @return A promise resolving to station group with billing info and pagination info.
     */
    async getStationGroup(stationGroupId, filters, sort) {
        const queryParams = toStationGroupDetailQueryParams(filters, sort);
        const url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/station-group/${stationGroupId}${queryParams}`;
        return axios
            .get(url, {
            headers: { "Content-Type": "application/json" }
        })
            .then(({ data: res }) => ({
            stationGroup: this.toStationGroup(res.data),
            totalCount: res.paging_info?.count ?? 0,
            pageToken: res.paging_info?.token
        }))
            .catch((error) => {
            throw new StationGroupsFetchFailed(error);
        });
    }
    saveItem(data, _options) {
        const hasValidProducts = data.billingInfo?.products?.some((p) => p.id && p.cpm != null);
        // station groups must have at least one valid product to update CPMs, otherwise the request will be rejected by the service
        const promise = hasValidProducts
            ? this.updateCpms(data)
            : Promise.reject(new StationGroupsSaveFailed(null, "No valid products to update"));
        return new Observable((observer) => {
            promise
                .then((stationGroup) => {
                this.cache.reconcile("save", stationGroup);
                observer.next(stationGroup);
                observer.complete();
            })
                .catch((error) => {
                observer.error(error instanceof StationGroupsSaveFailed ? error : new StationGroupsSaveFailed(error));
            });
        });
    }
    /**
     * Updates CPMs for multiple products within a station group.
     * @param stationGroup - Station group with products to update
     * @returns Updated station group with new CPM values
     */
    async updateCpms(stationGroup) {
        if (!stationGroup.id) {
            return Promise.reject(new StationGroupsSaveFailed(null, "Station group ID is required to update CPMs"));
        }
        const url = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/station-group-cpms`;
        const request = toServiceUpsertStationGroupCpmRequestFromStationGroup(stationGroup);
        await axios.post(url, request, { headers: { "Content-Type": "application/json" } });
        const detailUrl = `${this.sdk.urls.baseAPIUrl}/vertical-contracts/station-group/${stationGroup.id}`;
        const { data: res } = await axios.get(detailUrl, { headers: { "Content-Type": "application/json" } });
        return pricingStationGroupBillingInfoFromService(res.data);
    }
    /**
     * Converts from a service station group list item to a client station group list item.
     * @param stationGroup: the station group to convert.
     * @return: the converted station group (no billingInfo).
     */
    toStationGroupList(stationGroup) {
        return pricingStationGroupFromService(stationGroup);
    }
    /**
     * Converts from a station group to service format.
     * Flattens nested billingInfo to flat service structure.
     * @param stationGroup: the station group to convert.
     * @return: the converted station group.
     */
    toService(stationGroup) {
        return pricingStationGroupBillingInfoToService(stationGroup);
    }
    /**
     * Converts from a service station group detail to a client station group.
     * Unflattens to nested billingInfo structure.
     * @param stationGroup: the station group to convert.
     * @return: the converted station group (with billingInfo).
     */
    toStationGroup(stationGroup) {
        return pricingStationGroupBillingInfoFromService(stationGroup);
    }
    async deleteItem() {
        throw new NotImplementedError("delete");
    }
}
export default PricingStationGroups;
//# sourceMappingURL=index.js.map